/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IView, IViewInfo } from "../../../view/IView";
import { IEditor } from "../../../IEditor";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { ConfigEditorEditLayoutConfig } from "./Layout";
import { ConfigModel, DEFAULT_ENV_NAME } from "../Common/ConfigModel";
import { SessionStorageService } from "../../../sessionStorage/SessionStorageService";
import { AuthProvidersService } from "../../../authProviders/AuthProvidersService";
import { ResourceService } from "../../../resources/ResourceService";
import { ISchemaConstObjectOptsGroup } from "@adapptio/lib-blueprint";
import { AppConfigService } from "../../../config/AppConfigService";
import { IAppPublicKey } from "@adapptio/lib-core";

/**
 * View parameters
 */
interface IConfigEditorEditParams {
	resourceId: string;
}

const SSH_CATEGORY: ISchemaConstObjectOptsGroup = {
	id: "ssh",
	order: 9,
	label: "SSH"
};

/**
 * Error Log View
 */
export class ConfigEditorEdit implements IView<IConfigEditorEditParams> {
	/** Services */
	private readonly appConfigSvc: AppConfigService;
	private readonly sessionStorageSvc: SessionStorageService;
	private readonly authProvidersSvc: AuthProvidersService;
	private readonly resourceSvc: ResourceService;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/** Layout state */
	private layout = new LayoutState(ConfigEditorEditLayoutConfig);

	/** If resource was modified */
	public isModified: boolean;

	/** If the view is being saved */
	private saveInProgress = false;

	private configModel: ConfigModel;

	private publicKey: IAppPublicKey|null = null;

	private selection: { categoryId: string | null } = {
		categoryId: null
	};
	public readonly onSelectionChange = createEventEmitter();

	/** Categories are static from manifest schema, cached once */
	private categories: ISchemaConstObjectOptsGroup[] = [];

	/**
	 * View constructor
	 *
	 * @param dc Dependency container
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public constructor(private dc: IEditor) {
		this.appConfigSvc = dc.get("appConfigService");
		this.sessionStorageSvc = dc.get("sessionStorageService");
		this.authProvidersSvc = dc.get("authProvidersService");
		this.resourceSvc = dc.get("resourceService");
	}

	/**
	 * View init
	 */
	public async init(): Promise<void> {
		this.configModel = new ConfigModel(this.sessionStorageSvc, this.authProvidersSvc, this.resourceSvc);
		this.loadPublicKeys();

		await this.loadConfigModel();

		onEvent(this.configModel.onModelChange, this.handleModelChange);
	}

	private loadPublicKeys(): void {
		const appConfig = this.appConfigSvc.getConfig();
		this.publicKey = appConfig?.publicKey ?? null;
	}

	private async loadConfigModel(): Promise<void> {
		const manifest = this.appConfigSvc.getManifest();
		await this.configModel.loadDocument(manifest["content"]);
	}

	private handleModelChange = () => {
		this.setIsModified(true, true);
	};

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	private setSaveInProgress(saveInProgress: boolean) {
		this.saveInProgress = saveInProgress;
		emitEvent(this.onResourceInfoChanged);
	}

	public getConfigModel() {
		return this.configModel;
	}

	/**
	 * Returns categories from manifest schema (static, cached)
	 */
	public getCategories(): ISchemaConstObjectOptsGroup[] {
		if (this.categories.length < 1) {
			const configOpts = this.configModel.getOptsForEnvironment(DEFAULT_ENV_NAME);
			this.categories = configOpts?.schema.getPropGroups() || [];
		}

		if (this.categories.length > 1) {
			return [ ...this.categories, { ...SSH_CATEGORY } ];
		}

		return this.categories;
	}

	public getSelectedCategory(): string {
		const categories = this.getCategories();
		this.selection.categoryId = this.selection.categoryId || categories[0]?.id;

		return this.selection.categoryId;
	}

	public isSshCategorySelected(): boolean {
		return this.getSelectedCategory() === SSH_CATEGORY.id;
	}

	public selectCategory(categoryId: string): void {
		this.setSelection({ categoryId });
	}

	private setSelection(newSelection: Partial<typeof this.selection>): void {
		const updatedSelection = {
			...this.selection,
			...newSelection
		};

		if (
			updatedSelection.categoryId === this.selection.categoryId
		) {
			return;
		}

		this.selection = {
			...updatedSelection
		};

		emitEvent(this.onSelectionChange);
	}

	/**
	 * Returns if undo is available
	 */
	public canUndo(): boolean {
		return this.configModel.undoRedoManager.canUndo();
	}

	/**
	 * Returns if redo is available
	 */
	public canRedo(): boolean {
		return this.configModel.undoRedoManager.canRedo();
	}

	/**
	 * Undo last change
	 */
	public undo(): void {
		this.configModel.undoRedoManager.undo();
	}

	/**
	 * Redo last undone change
	 */
	public redo(): void {
		this.configModel.undoRedoManager.redo();
	}

	/**
	 * Saves changes
	 * Returns false if save is already in progress or no changes were made.
	 */
	public async save(): Promise<boolean> {
		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		this.setSaveInProgress(true);

		this.setIsModified(false, false);

		try {
			const content = this.configModel.exportDocumentAsYAML();
			await this.appConfigSvc.updateManifest(content);

			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.setSaveInProgress(false);
		}

		return true;
	}

	public canSave(): boolean {
		return this.isModified && !this.isBeingSaved();
	}

	public getPublicKey(): string | undefined {
		return this.publicKey?.publicKey;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			label: "App configuration",
			icon: "mdi/connection",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true
		};
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		removeAllEventListeners(this.onSelectionChange);
		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onResourceInfoChanged);

		offEvent(this.configModel.onModelChange, this.handleModelChange);
	}
}
